import { ApplicationRef, Component, EventEmitter, Injector, Input, OnInit, Output, OnDestroy, inject, signal } from '@angular/core';
import { Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AppGlobalsModule as _globals } from 'src/app/app-globals.module';
import { I18nService } from 'src/app/core/services/i18n/i18n.service';
import { SiteLocations } from 'src/app/core/services/url-address/url-address.service';
import { UserService } from 'src/app/user/user.service';
import { ThemeService } from 'src/app/core/services/theme/theme.service';
import { btnLink } from 'src/app/shared/helpers/btn-link/btn-link.model';

@Component({
    selector: 'header-menu',
    templateUrl: './header-menu.component.html',
    styleUrls: ['./header-menu.component.scss'],
    standalone: false,
})
export class HeaderMenuComponent implements OnInit, OnDestroy {
    private _i18n = inject(I18nService);
    private _userService = inject(UserService, { optional: true })!;
    private _appRef = inject(ApplicationRef);
    private _injector = inject(Injector);

    @Input() isMainMenuOpen: boolean = false;
    @Input() isAngularApp: boolean = false;
    // eslint-disable-next-line @angular-eslint/no-output-on-prefix
    @Output() onCloseMenu = new EventEmitter();
    public title: string = '';
    constructor() {
        this._langBaseUrl = this._i18n.getLangBaseUrl(true); // Why is it always set to Hebrew?
    }

    public groupsLinks: groupLinks[] = [];
    private _isHeb: boolean = false;
    @Input() FocusSearch: boolean = false;
    private _langBaseUrl: string;
    readonly freeSearchStr: string = $localize`:@@free-search:חיפוש חופשי`;
    public SiteLocations = SiteLocations;
    @Input() activeLocation!: SiteLocations;

    // in order to save number of times global search is opened
    public searchOpenedTimes: number = 0;

    //the nihul staff entries in the hamburger - same state machine as the sticky-header icons:
    //ניהול link + logout while connected as an internal user, a connect entry for a logged-out
    //browser that remembers a nihul logon address (the address itself never reaches the markup)
    public showNihulHome = signal(false);
    public showNihulLogout = signal(false);
    public showNihulLogin = signal(false);
    //the theme control (dark mode test phase): staff with cms access, on angular pages only -
    //the header is esi-included on legacy pages too, whose bodies don't follow the theme
    public showThemeSwitch = signal(false);
    /** where the כניסה לניהול entry lands - rabbi-only accounts go straight to their area */
    public nihulHomeLink = signal('/nihul');
    public isHeb = false;
    private _subscriptions: Subscription[] = [];

    /** the nihul lives on the hebrew domain only - absolute from the english site */
    public get nihulHref(): string {
        return `${environment.nihulDomain}${this.nihulHomeLink()}`;
    }

    ngOnInit(): void {
        this._isHeb = this._i18n.langIsHeb();
        this.isHeb = this._isHeb;
        this.groupsLinks = this.getGroupsLinks();
        if (!_globals.isServer && this._userService) {
            //follow every login/logout (including a nihul-overlay login and the focus-time
            //transfer-cookie sync); logged-out users never get an emission, so userReady
            //supplies the initial state
            this._subscriptions.push(
                this._userService.getUser().subscribe(() => {
                    this._updateNihulEntries();
                }),
            );
            this._userService.userReady(false).then(() => {
                this._updateNihulEntries();
            });
        }
    }

    private _updateNihulEntries(): void {
        let hasLogonAddress = false;
        try {
            hasLogonAddress = !!localStorage.getItem('nihulLogonUrl');
        } catch (e) {
            //storage unavailable - no entry
        }
        const connected = this._userService.userConnected();
        const isInternal = this._userService.isInternal();
        //rabbi accounts (nihulUser without internal access) get the entry too, pointed at
        //their own area by nihulEntryLink
        this.showNihulHome.set(connected && (isInternal || this._userService.nihulUser()));
        this.nihulHomeLink.set(this._userService.nihulEntryLink());
        this.showNihulLogout.set(connected && isInternal);
        this.showNihulLogin.set(hasLogonAddress && !connected);
        const showThemeSwitch = this.isAngularApp && this._userService.hasInternalAccess();
        this.showThemeSwitch.set(showThemeSwitch);
        if (showThemeSwitch) {
            //staff land in dark by default (test phase); via the injector so legacy pages never construct the service
            this._injector.get(ThemeService).seedStaffDefault();
        }
        this._tick();
    }

    private _tick(): void {
        //zoneless build: this runs from legacy UserBar callbacks, outside angular's reactive
        //context - signal writes there don't schedule a tick on the production build
        try {
            this._appRef.tick();
        } catch (e) {
            //already inside change detection - the write is picked up anyway
        }
    }

    /** opens the nihul logon overlay (UserBar) from the remembered address */
    public openNihulLogin(): void {
        const userGlobal = (window as { user?: { nihulLogin?: () => boolean } }).user;
        userGlobal?.nihulLogin?.();
    }

    public getGroupsLinks(): groupLinks[] {
        return [
            {
                title: { name: $localize`:@@home:ראשי`, link: '/', isAngularPage: true },
                btnLinks: [
                    { name: $localize`:@@dedications:תרומות`, link: '/dedication' },
                    {
                        name: $localize`:@@parashatHashavua:פרשת השבוע`,
                        link: `/tags/${$localize`:@@parashatHashavua:פרשת השבוע`}`,
                    },
                    { name: $localize`:@@articles:כתבות`, link: '/article', isAngularPage: true },
                    {
                        name: $localize`:@@betElYeshivaCenter:קרית הישיבה בית אל`,
                        link: 'https://www.ybe.org.il/',
                    },
                    // Return when there will be a working page
                    // { name: $localize`:@@daily-halacha:הלכה יומית`, link: '/message/services.asp?service=7' },
                    {
                        name: $localize`:@@content-for-your-site:תכנים לאתרך`,
                        link: '/generalpages/externalcontent?the_content=time',
                    },
                    {
                        name: this._isHeb ? 'English' : 'עברית',
                        link: _globals.otherLanguageHost(this._i18n.getLang()),
                        className: (this._isHeb ? 'eng' : 'heb') + '-letters',
                        iconName: 'language',
                    },
                    { name: $localize`:@@rabbinicalRulings:פסקי דין רבניים`, link: '/psakim' },
                    { name: $localize`:@@yeshiva-web-app:אפליקצית אתר ישיבה`, link: '/about/yeshivaapp' },
                    { name: $localize`:@@about:אודות`, link: '/about' },
                ],
            },
            {
                title: { name: $localize`:@@beitMidrash:בית מדרש`, link: '/midrash', isAngularPage: true },
                btnLinks: [
                    { name: $localize`:@@lessons:שיעורים`, link: '/midrash/filter', isAngularPage: true },
                    { name: $localize`:@@shortsMenuLink:שורטים`, link: '/midrash/shorts', isAngularPage: true },
                    { name: $localize`:@@roadLessons:שיעור לדרך`, link: '/midrash/roadlessons', isAngularPage: true },
                    {
                        name: $localize`:@@videoLessons:שיעורי וידאו`,
                        link: '/midrash/filter',
                        queryParams: { filter: 'video' },
                        isAngularPage: true,
                    },
                    {
                        name: $localize`:@@audioLessons:שיעורי שמע`,
                        link: '/midrash/filter',
                        queryParams: { filter: 'audio' },
                        isAngularPage: true,
                    },
                    { name: $localize`:@@yeshivaChanel:ערוץ ישיבה`, link: '/lvideo/' },
                    { name: $localize`:@@library:ספריה`, link: '/sifria' },
                    { name: $localize`:@@series:סדרות`, link: '/midrash/series' },
                    { name: $localize`:@@testYourself:בחן את עצמך`, link: '/test' },
                    {
                        name: $localize`:@@pnineiHalacha:פניני הלכה`,
                        link: '/midrash/category/149',
                        isAngularPage: true,
                    },
                    { name: $localize`:@@yeshivaRabbis:רבני ישיבת בית אל`, link: '/midrash/yberavs' },
                ],
            },

            {
                title: { name: $localize`:@@calendar:לוח שנה`, link: '/calendar/' },
                btnLinks: [
                    { name: $localize`:@@shabatTimes:זמני כניסת השבת`, link: '/calendar/shabbattimes' },
                    { name: $localize`:@@daily-times:זמני היום`, link: '/calendar/timesday' },
                    { name: $localize`:@@monthTimes:זמני החודש`, link: '/calendar/timestable' },
                    { name: $localize`:@@moladot:זמן המולד`, link: '/calendar/moladot/' },
                    { name: $localize`:@@memorial-calendar:לוח ימי זכרון`, link: '/events/memorial', isAngularPage: true },
                    { name: $localize`:@@events:אירועים`, link: '/events/activities' },
                    { name: $localize`:@@bar-mitzva-calculator:מחשבון בר מצוה`, link: '/calendar/bmcal' },
                    {
                        name: $localize`:@@date-converter:ממיר תאריכים עברי לועזי`,
                        link: '/calendar/dateconverter',
                        isAngularPage: true,
                    },

                    {
                        name: $localize`:@@principles-times:עקרונות זמני היום`,
                        link: '/calendar/timeprinciples',
                    },
                    {
                        name: $localize`:@@timesOnYourSite:זמני היום באתרך`,
                        link: '/generalpages/externalcontent?the_content=time',
                    },
                ],
            },
            {
                title: {
                    name: $localize`:@@wiki:ויקישיבה`,
                    link: (this._isHeb ? '' : this._langBaseUrl) + '/wiki/',
                },
                btnLinks: [
                    {
                        name: $localize`:@@talmudicEncyclopedia:האנציקלופדיה התלמודית`,
                        link: (this._isHeb ? '' : this._langBaseUrl) + '/wiki/index.php/ויקישיבה:אנציקלופדיה_תלמודית',
                    },
                    {
                        name: $localize`:@@parsheni:פרשני`,
                        link: (this._isHeb ? '' : this._langBaseUrl) + '/wiki/index.php/פרשני:עמוד_ראשי',
                    },
                    {
                        name: $localize`:@@darsheni:דרשני`,
                        link: (this._isHeb ? '' : this._langBaseUrl) + '/wiki/index.php/דרשני:עמוד_ראשי',
                    },
                    {
                        name: $localize`:@@signup:הרשמה`,
                        link: (this._isHeb ? '' : this._langBaseUrl) + '/wiki/index.php/מיוחד:הרשמה_לחשבון?returnto=עמוד_ראשי',
                    },
                    {
                        name: $localize`:@@random-page:דף אקראי`,
                        link: (this._isHeb ? '' : this._langBaseUrl) + '/wiki/index.php/מיוחד:אקראי',
                    },
                    {
                        name: $localize`:@@dictionary:מילון`,
                        link: (this._isHeb ? '' : this._langBaseUrl) + '/wiki/index.php/מילון:ראשי',
                    },
                ],
            },
            {
                title: { name: $localize`:@@askTheRabbi:שאל את הרב`, link: '/ask', isAngularPage: true },
                btnLinks: [
                    {
                        name: $localize`:@@questions-repository:מאגר השאלות`,
                        link: '/ask/filter',
                        isAngularPage: true,
                    },
                    { name: $localize`:@@askYourQuestion:שליחת שאלה`, link: '/ask/ask' },
                    {
                        name: $localize`:@@familyPurity:בית הוראה לטהרת המשפחה`,
                        link: '/ask/askhoraa?inner=2',
                    },
                    {
                        name: $localize`:@@halaha-for-your-site:הלכה לאתרך`,
                        link: '/generalpages/externalcontent?the_content=ask',
                    },
                    {
                        name: $localize`:@@daily-halacha:הלכה יומית`,
                        link: '/ask/halacha',
                    },
                ],
            },
            {
                title: { name: $localize`:@@contact:צור קשר`, link: '/contact' },
                btnLinks: [
                    {
                        name: $localize`:@@advertiseWithUs:פרסם אצלנו`,
                        link: (this._isHeb ? '' : this._langBaseUrl) + '/about/advertise',
                    },
                    { name: $localize`:@@web-development:בניית אתרים`, link: '/build' },
                    { name: $localize`:@@vacationAndSaturdays:נופש ושבתות אתר ישיבה`, link: '/sales' },
                ],
            },
            {
                title: { name: $localize`:@@selfArea:איזור אישי`, link: '/users' },
                btnLinks: [
                    { name: $localize`:@@login:התחברות`, link: '/users/' },
                    { name: $localize`:@@signup:הרשמה`, link: '/users/details/' },
                    { name: $localize`:@@termsAndConditions:תקנון`, link: '/terms', isAngularPage: true },
                ],
            },
        ];
    }

    ngOnDestroy() {
        this._subscriptions.forEach((subscription) => subscription?.unsubscribe());
        if (this.onCloseMenu) this.onCloseMenu.unsubscribe();
    }
}

interface groupLinks {
    title: btnLink;
    btnLinks: btnLink[];
}
